import { Loader2, AlertTriangle, Sparkles } from "lucide-react"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/modules/shared/ui/components/dialog"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Sage300Vacancy } from "@/modules/recruitment/infrastructure/sage300-service"

interface Sage300VacanciesModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vacancies: Sage300Vacancy[]
  isLoading: boolean
  error: string | null
  onGenerateAI: (positionId: number) => Promise<void>
  generatingPositionId: number | null
}

export function Sage300VacanciesModal({
  open,
  onOpenChange,
  vacancies,
  isLoading,
  error,
  onGenerateAI,
  generatingPositionId,
}: Sage300VacanciesModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Sage 300 Open Vacancies</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex flex-col items-center justify-center py-10 gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Fetching vacancies from Sage 300...
            </p>
          </div>
        )}

        {!isLoading && error && (
          <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <span>{error}</span>
          </div>
        )}

        {!isLoading && !error && vacancies.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No open vacancies found in Sage 300.
          </p>
        )}

        {!isLoading && !error && vacancies.length > 0 && (
          <div className="overflow-auto max-h-[420px] rounded-md border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">
                    Position Code
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">
                    Company Code
                  </th>
                  <th className="px-4 py-3 text-left font-medium">
                    Company Rule
                  </th>
                  <th className="px-4 py-3 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vacancies.map((v) => {
                  const isThisGenerating = generatingPositionId === v.positionId
                  const isAnyGenerating = generatingPositionId !== null
                  return (
                    <tr key={v.positionId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">
                        {v.positionCode}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {v.description}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className="bg-green-100 text-green-700 rounded-sm">
                          {v.positionStatus}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {v.companyCode}
                      </td>
                      <td className="px-4 py-3 text-gray-900">
                        {v.companyRuleCode}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isAnyGenerating}
                          onClick={() => onGenerateAI(v.positionId)}
                          className="flex items-center gap-1.5 whitespace-nowrap"
                        >
                          {isThisGenerating ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Sparkles className="h-3.5 w-3.5" />
                          )}
                          {isThisGenerating
                            ? "Generating..."
                            : "Generate AI Vacancy"}
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
